import { useEntityNotes } from '../../components/entity-notes/useEntityNotes';
import { inNotesCell, notesGridSx } from '../../components/entity-notes/notesColumn';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  Skeleton, Typography,
} from '@mui/material';
import { Add, ArrowBack, ContentCopy, Edit, ExpandMore, Visibility, VisibilityOff } from '@mui/icons-material';
import FlowTrackingUrlsAction from '../../components/FlowTrackingUrlsAction';
import TooltipIconButton from '../../components/TooltipIconButton';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { DataGridPremium, GridRenderCellParams, GridRowParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { trafficSourcesService } from '../../services/traffic-sources.service';
import { flowsService, Flow } from '../../services/flows.service';
import { adAccountsService } from '../../services/ad-accounts.service';
import { activityOrderBy } from '../../components/ActivityCell';
import { flowsColumns } from '../flows/flows.columns';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import TrafficSourceForm from './TrafficSourceForm';

const EDITABLE_FIELDS = ['traffic_cap_limit'];

export default function TrafficSourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('flow');
  const isDuplicate = !!useMatch('/traffic-sources/:id/duplicate');
  const isNew = !id;
  const isCreateMode = isNew || isDuplicate;
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);

  const { data: trafficSource, isLoading } = useQuery({
    queryKey: ['traffic-source', id],
    queryFn: () => trafficSourcesService.getById(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const [childSearch, setChildSearch] = useState('');
  const debouncedChildSearch = useDebounced(childSearch);
  const [statuses, setStatuses] = useState<string[]>(['active']);
  const flowsWhere = useMemo(
    () => buildEntityWhere('flow', { search: debouncedChildSearch, statuses, tagIds: [] }, ['name'],
      [{ flow: { traffic_source_id: { op: '=', value: id } } }]),
    [id, debouncedChildSearch, statuses],
  );
  const { gridProps, offset, limit } = useServerPagination(50, flowsWhere);
  const [flowsSort, setFlowsSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);
  const flowsOrderBy = activityOrderBy(flowsSort);
  const gridHeight = useGridHeight();
  const showChildren = !isCreateMode && !!trafficSource;
  const { data: adAccounts } = useQuery({
    queryKey: ['ad-accounts', 'for-flows'],
    queryFn: () => adAccountsService.query({ limit: 200 }),
  });
  const { data: flowsResult, isFetching: flowsLoading } = useQuery({
    queryKey: ['flows', 'by-traffic-source', id, flowsWhere, offset, limit, flowsSort],
    queryFn: () => flowsService.query({ where: flowsWhere, offset, limit,
      ...(flowsOrderBy && { order_by: flowsOrderBy, order: flowsSort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    enabled: showChildren,
    placeholderData: (prev) => prev,
  });

  const invalidateFlows = () => queryClient.invalidateQueries({ queryKey: ['flows'] });
  const notes = useEntityNotes('flow');

  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, flowsResult?.data || []), [selection, flowsResult]);
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidateFlows(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((sid) => flowsService.delete(sid))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((sid) => flowsService.unarchive(sid))), `Unarchived ${selectedIds.length}`);

  const processRowUpdate = async (newRow: Flow, oldRow: Flow): Promise<Flow> => {
    newRow = await notes.saveIfChanged(newRow, oldRow);
    if (newRow.traffic_cap_limit === oldRow.traffic_cap_limit) return newRow;
    const cap = Number(newRow.traffic_cap_limit);
    if (!Number.isFinite(cap) || cap < 0) throw new Error('Traffic cap must be 0 or greater');
    await flowsService.update(oldRow.id, { traffic_cap_limit: cap });
    invalidateFlows();
    enqueueSnackbar('Updated', { variant: 'success' });
    return { ...newRow, traffic_cap_limit: cap };
  };
  const onProcessRowUpdateError = (e: unknown) => enqueueSnackbar(e instanceof Error ? e.message : 'Update failed', { variant: 'error' });

  const flowColumns = flowsColumns({
    notes: notes.column,
    includeTrafficSource: false,
    adAccounts: adAccounts?.data,
    actions: {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Edit" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update', hideParent: true })}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Duplicate" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate', hideParent: true })}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <FlowTrackingUrlsAction id={params.row.id} />
        </Box>
      ),
    },
  });

  if (!!id && isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!!id && !isLoading && !trafficSource) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/traffic-sources')} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">Traffic source not found.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1 }}>
          {isNew ? 'New Traffic Source' : isDuplicate ? 'Duplicate Traffic Source' : trafficSource?.name}
        </Typography>
        {!isCreateMode && trafficSource && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CopyId id={trafficSource.id} />
            <HistoryButton entityType="traffic_sources" entityId={trafficSource.id} entityName={trafficSource.name} />
          </Box>
        )}
      </Box>

      <Accordion defaultExpanded={isCreateMode}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight={600}>Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TrafficSourceForm
            trafficSource={isNew ? undefined : trafficSource}
            duplicate={isDuplicate}
            onDirtyChange={setDirty}
            onSaved={(saved) => { if (isCreateMode) { flushSync(() => setDirty(false)); navigate(`/traffic-sources/${saved.id}`); } }}
          />
        </AccordionDetails>
      </Accordion>

      {showChildren && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Flows</Typography>
          </Box>
          <DataGridPremium
            rows={flowsResult?.data || []}
            columns={flowColumns}
            sortingMode="server"
            sortModel={flowsSort}
            onSortModelChange={setFlowsSort}
            loading={flowsLoading}
            {...gridProps(flowsResult?.total)}
            showToolbar
            slots={{ toolbar: EntityGridToolbar }}
            slotProps={{ toolbar: {
              search: childSearch, onSearch: setChildSearch,
              statuses: { value: statuses, onChange: setStatuses },
              actions: [{ icon: Add, hint: 'Add flow', onClick: () => setModal({ open: true, reason: 'Create', defaults: { traffic_source_id: id }, hideParent: true }) }],
              selectedCount: selectedIds.length,
              pluralName: 'flows',
              selectionActions: [
                { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
                { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
              ],
            } }}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            keepNonExistentRowsSelected
            disableColumnSorting
            disableColumnFilter
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
        onCellEditStart={notes.onCellEditStart}
        disableClipboardPaste
            onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
              const target = event.target as HTMLElement;
              if (target.closest('[data-field="actions"]')) return;
              if (EDITABLE_FIELDS.some((f) => target.closest(`[data-field="${f}"]`))) return;
              if (notes.editable && inNotesCell(target)) return;
              setModal({ open: true, id: params.row.id, reason: 'Update', hideParent: true });
            }}
            sx={{ ...notesGridSx, bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
          />
        </Box>
      )}
    </Box>
  );
}
